<script setup>
  import { ref, computed, watchEffect, defineExpose, useTemplateRef } from 'vue';
  import GraphCalendarWeek from "@/components/GraphCalendarWeek.vue";
  import Toggle from "@/components/controls/Toggle.vue";
  import ErrorBoundry from '@/components/ErrorBoundry.vue';
  import { useGraphCalendarController } from "@/composables/useGraphCalendarController.js";
  import { format } from 'date-fns'

  const emits = defineEmits(['update:selectedDate', 'update:startDate', 'update:endDate', 'changed:calendarView', 'changed:monthView']);

  const props = defineProps({
    selectedDate: {
      type: Date,
      required: false,
      default: () => new Date()
    },
    graphComponent: {
      type: Object,
      required: true
    },
    calendarViewChangeCallback: {
      type: Function,
      required: false,
      default: null
    },
    dataReady: {
      type: Boolean,
      default: true
    },
    storeId: {
      type: Number,
      default: null
    }
  });

  const graph = useTemplateRef('graph');
  const errorBoundry = useTemplateRef('error-boundry');

  const { startDate, endDate, currentCalendarView, calendarWeeks, selectedCalendarDate, updateSelectedCalendarDate, addDay, addWeek, addMonth, addYear, subtractDay, subtractWeek, subtractMonth, subtractYear, today } = useGraphCalendarController({  calendarViewChangeCallback: props.calendarViewChangeCallback });

  const plotLeftOffset = ref(0);
  const plotTopOffset = ref(0);
  const plotHeight = ref(0);

  const startDateFormatted = computed(() => format(startDate.value, 'LLL dd, yyyy'));
  const endDateFormatted = computed(() => format(endDate.value, 'LLL dd, yyyy'));

  const graphCanReload = computed(() => errorBoundry.value && typeof errorBoundry.value.reset === 'function' || graph.value && typeof graph.value.reload === 'function');

  const styles = computed(() => {
    return {
      '--jwx-graph-calendar-plot-left-offset': `${plotLeftOffset.value}px`,
      '--jwx-graph-calendar-plot-top-offset': `${plotTopOffset.value}px`,
      '--jwx-graph-calendar-plot-height': `${plotHeight.value}px`
    };
  });

  function handleMonthViewChange(e) {
    emits('changed:monthView', e);
  }

  watchEffect(() => {
    emits('changed:calendarView', { calendarView: currentCalendarView.value, weeks: calendarWeeks.value });
  });

  watchEffect(() => {
    emits('update:selectedDate', selectedCalendarDate.value);
  });

  defineExpose({ updateSelectedCalendarDate, addDay, addWeek, addMonth, addYear, subtractDay, subtractWeek, subtractMonth, subtractYear, today});
</script>

<template>
  <div
    v-show="props.dataReady"
    class="jwx-graph-calendar"
    :style="styles"
    :data-calendar-view="currentCalendarView"
  >
    <div v-if="calendarWeeks.length > 0" class="jwx-graph-calelndar__header">
      <p class="jwx-graph-calendar__date-range">{{startDateFormatted}} - {{endDateFormatted}}</p>
      <Toggle
        @input:change="handleMonthViewChange"
        id="graph-calendar-view-toggle"
        class="jwx-graph-calendar__view-toggle"
        value="week"
        v-model="currentCalendarView"
        trueValue="month"
        falseValue="week"
        size="small"
        data-background="surface"
      >Month View</Toggle>
    </div>

    <div class="jwx-graph-calendar__body">
      <div class="jwx-graph-calendar__placeholder-container">
        <div v-for="(week, index) in calendarWeeks" :key="index" class="jwx-graph-calendar__week-placeholder"></div>
      </div>      
      <div class="jwx-graph-calendar__weeks-container">
        <GraphCalendarWeek
          v-for="(week) in calendarWeeks"
          @update:selectedDate="updateSelectedCalendarDate($event)"
          :key="`${week.startDate.getTime() + week.endDate.getTime()}`"
          :selectedDate="selectedCalendarDate"
          :startDate="week.startDate"
          :endDate="week.endDate"
        >
        {{ graphCanReload }}
        <template #graph>
          <ErrorBoundry ref="error-boundry" :propagate="false" >
            <component 
              ref="graph"
              :is="graphComponent" 
              v-model:plotLeftOffset="plotLeftOffset"
              v-model:plotTopOffset="plotTopOffset"
              v-model:plotHeight="plotHeight"
              :startDate="week.startDate" 
              :endDate="week.endDate" 
              :containerId="`tide-calendar-week-graph-${week.startDate.getTime()}-${week.endDate.getTime()}`"
              :weekCount="calendarWeeks.length"
              :storeId="props.storeId"
            />
            <template #fallback>
                <div class="jwx-graph-calendar__error">
                  <p class="jwx-graph-calendar__error-message">
                    <slot v-if="graphCanReload" name="error-message" :error="error">There was an error loading week's data. <button @click="graph.value.reload()">Retry</button></slot>
                    <slot v-else name="error-message" :error="error">There was an error loading week's data.</slot>
                  </p>
                </div>
            </template>
          </ErrorBoundry>
        </template>
        </GraphCalendarWeek>
      </div>
    </div>
  </div>
</template>